import React, { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useViewConfig } from '../hooks/useViewConfig';
import HomeSections from '../components/HomeSections/HomeSections';
import HomeSectionsSkeleton from '../components/HomeSections/HomeSectionsSkeleton';
import HomeCategories from '../components/HomeCategories/HomeCategories';
import HomeFeatures from '../components/HomeFeatures/HomeFeatures';
import '../styles/product.css';

const Home = () => {
  const { hash } = useLocation();

  // Fetch HOME_SECTIONS from ViewConfig API using the custom hook
  const { data: configData, loading: loadingSections, error: sectionsError } = useViewConfig("HOME_SECTIONS");
  const [sections, setSections] = useState([]);

  // Smooth scroll logic based on window.location.hash with sticky navbar offset compensation
  useEffect(() => {
    if (hash) {
      const targetId = hash.replace('#', '');
      if (targetId === 'home-top') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        const element = document.getElementById(targetId);
        if (element) {
          // Slight delay to allow dynamic categories or sections to finish mounting
          const timer = setTimeout(() => {
            const navbarHeight = 90; // offset to prevent overlapping sticky navbar
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.scrollY - navbarHeight;
            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });
          }, 150);
          return () => clearTimeout(timer);
        }
      }
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [hash]);

  // Handle section data processing, sorting, and error logging
  useEffect(() => {
    if (configData && configData.data && configData.data.sections) {
      // Create a shallow copy before sorting to avoid mutating read-only hook state
      const sorted = [...configData.data.sections].sort(
        (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)
      );
      setSections(sorted);
    }
  }, [configData]);

  useEffect(() => {
    if (sectionsError) {
      console.error("Failed to load HOME_SECTIONS configuration:", sectionsError);
    }
  }, [sectionsError]);

  return (
    <div className="home-container" id="home-top">
      <div className="hero-banner">
        <div className="hero-left">
          <h1 className="hero-heading">
            <span className="hero-line">Welcome to</span>
            <br />
            <span className="hero-brand">REGARD TRADERS</span>
          </h1>
          <p className="hero-subtitle">Your trusted wholesale partner for premium products at unbeatable prices.</p>
          <div className="hero-buttons">
            <Link to="/#shop-sections" className="btn btn-primary">Browse Sections</Link>
            <Link to="/shop" className="btn btn-secondary">View All Products</Link>
          </div>
        </div>
        <div className="hero-right">
          <div className="hero-image-glow" aria-hidden="true"></div>
          <img src="/home_banner.png" alt="Products showcase" className="hero-image" />
        </div>
      </div>

      <div className="section-header" id="shop-sections">
        <p>SHOP BY SECTION</p>
        <p>Explore products across our top sections</p>
      </div>

      {/* Dynamic ViewConfig-driven Home Sections */}
      {loadingSections ? (
        <HomeSectionsSkeleton />
      ) : (
        !sectionsError && sections.length > 0 && <HomeSections sections={sections} />
      )}

      {/* Dynamic ViewConfig-driven Home Categories */}
      <div id="popular-categories">
        <HomeCategories />
      </div>

      {/* Value Propositions / Features Section */}
      <HomeFeatures />
    </div>
  );
};

export default Home;
